/**
 * How a customer's booking/GDS account codes read on screen.
 *
 * The API sends three fields (see customer-management-api lib/account-codes.ts):
 * `account_number` — the ONE code, or null when the customer has none or many;
 * `account_codes` — all of them; `additional_account_codes` — all of them minus
 * the customer id, which is already on screen next to it.
 *
 * This file is only about the second line of a customer cell, where there is
 * room for roughly one short phrase. The rule is: say the codes when saying them
 * fits, and say how many when it doesn't. Western Washington University has 43
 * of them, so "43 account codes" is the whole of what that line can usefully
 * carry — the codes themselves belong on the profile, where they can wrap.
 */

/** Above this many extra codes, the line reports a count instead of listing. */
const INLINE_LIMIT = 2;

/**
 * The meta line under a customer's name: the id, plus whatever the account
 * codes still have to add.
 *
 * Never renders a separator with nothing after it. That was the previous
 * behaviour for the 643 of 2,375 customers with no account number recorded —
 * `1001180500 · ` — which reads as a value that failed to load rather than a
 * customer that has none.
 */
export function customerMetaLine(
  customerId: string,
  additionalCodes: string[] | undefined,
): string {
  const extra = additionalCodes ?? [];
  if (extra.length === 0) return customerId;
  if (extra.length <= INLINE_LIMIT) {
    return `${customerId} · ${extra.join(' · ')}`;
  }
  return `${customerId} · ${extra.length} account codes`;
}

/**
 * The account codes as one CSV cell.
 *
 * Joined with `; ` rather than `, ` so the value stays readable when a
 * spreadsheet shows it — the comma is what the source used, and it is also the
 * delimiter of the file it is being written into.
 */
export function accountCodesCell(codes: string[] | undefined): string {
  return (codes ?? []).join('; ');
}
